'use client';
import React, { useEffect, useMemo, useState } from 'react';
import { getCookie } from 'cookies-next';
import { useHits, InstantSearch, Configure } from 'react-instantsearch';
import { ProjectSettings } from 'shared/types/projectSettings/ProjectSettings';
import { Swiper, SwiperSlide } from 'swiper/react';
import ProductItem from '@/components/customercenter-ui/components/home/productListCarousel/productItem';
import Loader from '@/customercenter-ui/components/ui/loader';
import { useProjSettings } from '@/frontastic/hooks/useProjectSettings/useProjectSettings';
import { createAlgoliaLiteClient, wrapInstantSearchClient } from '@/helpers/algolia/algoliaV5';
import { swiperConfig } from '@/helpers/utils/swiperConfig';
import { COOKIE_KEYS } from '../../../../../constants/constant';

const UPSELL_MIN_HITS_PER_PAGE = 8;

const createSearchClient = (projectSettings: ProjectSettings) =>
  wrapInstantSearchClient(createAlgoliaLiteClient(projectSettings?.algoliaAppId, projectSettings?.algoliaApiKey));

const UpsellAlgoliaCarousel = () => {
  const { hits } = useHits();
  const [hasLoadedHits, setHasLoadedHits] = useState(false);

  useEffect(() => {
    if (hits?.length) {
      setHasLoadedHits(true);
    }
  }, [hits]);

  if (!hasLoadedHits && !hits?.length) {
    return <Loader />;
  }
  const slidesPerGroup = 4;

  const blankSlidesCount = hits.length % slidesPerGroup === 0 ? 0 : slidesPerGroup - (hits.length % slidesPerGroup);

  return (
    <Swiper {...swiperConfig} className="mySwiper multiSlide" observer observeParents>
      {hits?.map((hit: any) => (
        <SwiperSlide key={hit?.objectID}>
          <ProductItem product={hit} upSell={true} />
        </SwiperSlide>
      ))}

      {Array.from({ length: blankSlidesCount }).map((_, index) => (
        <SwiperSlide key={`blank-slide-${index}`}>
          <div className="h-full min-h-[1px]" />
        </SwiperSlide>
      ))}
    </Swiper>
  );
};

const UpsellWidgetCarousel: React.FC<any> = ({ data }) => {
  const projectSettings: ProjectSettings = useProjSettings();
  const searchClient = useMemo(() => createSearchClient(projectSettings), [projectSettings]);

  const indexName = useMemo(() => {
    const currentSite = getCookie(COOKIE_KEYS.selectedSite);
    switch (data?.widgetData?.selectOption) {
      case 'productWidget':
        return `products_${currentSite}`;
      case 'categoryWidget':
        return `categories_${currentSite}`;
      case 'brandWidget':
        return `brands_${currentSite}`;
      default:
        return `products_${currentSite}`;
    }
  }, [data?.selectOption]);

  const hitsPerPage = useMemo(() => {
    const configured = data?.widgetData?.maxWidgetItemsPerPage || 5;
    return Math.max(configured, UPSELL_MIN_HITS_PER_PAGE);
  }, [data?.widgetData?.maxWidgetItemsPerPage]);

  return (
    <InstantSearch
      indexName={indexName}
      routing={false}
      insights={false}
      key={'algolia-upsell-client'}
      future={{
        persistHierarchicalRootCount: true,
        preserveSharedStateOnUnmount: true,
      }}
      searchClient={searchClient}
    >
      <Configure query={data?.widgetData?.widgetQuery} hitsPerPage={hitsPerPage} clickAnalytics={true} />
      <UpsellAlgoliaCarousel />
    </InstantSearch>
  );
};

export default UpsellWidgetCarousel;
